import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  FileText,
  Plus,
  Search,
  Send,
  RefreshCw,
  Ban,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  X,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Invoice, type InvoiceDetail, type Room } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { FilterTabs } from '../components/FilterTabs'

// ============================================================================
// STATUS CONFIG
// ============================================================================

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; icon: typeof CheckCircle; bgColor: string }
> = {
  draft: {
    label: 'Draft',
    color: 'text-gray-600',
    icon: FileText,
    bgColor: 'bg-gray-100',
  },
  sent: {
    label: 'Sent',
    color: 'text-blue-600',
    icon: Send,
    bgColor: 'bg-blue-100',
  },
  paid: {
    label: 'Paid',
    color: 'text-green-600',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
  },
  overdue: {
    label: 'Overdue',
    color: 'text-red-600',
    icon: AlertCircle,
    bgColor: 'bg-red-100',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-gray-500',
    icon: XCircle,
    bgColor: 'bg-gray-100',
  },
  refunded: {
    label: 'Refunded',
    color: 'text-teal-600',
    icon: CreditCard,
    bgColor: 'bg-teal-100',
  },
}

// ============================================================================
// GUEST PICKER
// ============================================================================

interface GuestOption {
  id: string
  name: string
  email: string
  guestType: string
  discountPercent: number | null
}

function GuestPicker({
  value,
  onChange,
}: {
  value: GuestOption | null
  onChange: (guest: GuestOption | null) => void
}) {
  const [search, setSearch] = useState('')
  const [guests, setGuests] = useState<GuestOption[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.guests
      .list()
      .then((data: any) => {
        setGuests(
          (data.guests || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            email: g.email,
            guestType: g.guestType || 'guest',
            discountPercent: g.discountPercent,
          }))
        )
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (!search) return guests
    const q = search.toLowerCase()
    return guests.filter(
      (g) => g.name.toLowerCase().includes(q) || g.email.toLowerCase().includes(q)
    )
  }, [guests, search])

  if (value) {
    return (
      <div className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
        <div className="flex-1">
          <div className="font-medium text-gray-900">{value.name}</div>
          <div className="text-sm text-gray-500">{value.email}</div>
          {value.discountPercent && value.discountPercent > 0 ? (
            <span className="text-xs text-teal-600">
              {value.discountPercent}% discount
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search guests by name or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No guests found</div>
          ) : (
            filtered.map((g) => (
              <button
                key={g.id}
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                onClick={() => {
                  onChange(g)
                  setSearch('')
                  setOpen(false)
                }}
              >
                <div className="font-medium text-gray-900 text-sm">{g.name}</div>
                <div className="text-xs text-gray-500">{g.email}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// LINE ITEM EDITOR
// ============================================================================

interface LineItemInput {
  roomId: string
  startTime: string
  endTime: string
  date: string
}

function LineItemEditor({
  items,
  onChange,
  rooms,
}: {
  items: LineItemInput[]
  onChange: (items: LineItemInput[]) => void
  rooms: Room[]
}) {
  const addItem = () => {
    onChange([...items, { roomId: rooms[0]?.id || '', startTime: '09:00', endTime: '10:00', date: '' }])
  }

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx))
  }

  const updateItem = (idx: number, field: keyof LineItemInput, value: string) => {
    const updated = [...items]
    updated[idx] = { ...updated[idx]!, [field]: value }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Room usage {idx + 1}
            </span>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Room</label>
              <select
                value={item.roomId}
                onChange={(e) => updateItem(idx, 'roomId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a room...</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.hourlyRate ? `£${r.hourlyRate}/hr` : 'Free'})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={item.date}
                onChange={(e) => updateItem(idx, 'date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Start</label>
                <input
                  type="time"
                  value={item.startTime}
                  onChange={(e) => updateItem(idx, 'startTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">End</label>
                <input
                  type="time"
                  value={item.endTime}
                  onChange={(e) => updateItem(idx, 'endTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
          {item.roomId && item.date && item.startTime && item.endTime && (
            <div className="text-sm text-gray-500">
              {(() => {
                const room = rooms.find((r) => r.id === item.roomId)
                const rate = room?.hourlyRate || 0
                const start = new Date(`${item.date}T${item.startTime}`)
                const end = new Date(`${item.date}T${item.endTime}`)
                const hours = (end.getTime() - start.getTime()) / 3600000
                if (hours <= 0) return 'End time must be after start time'
                const total = Math.round(hours * rate * 100) / 100
                return `${hours.toFixed(1)} hours x £${rate.toFixed(2)} = £${total.toFixed(2)}`
              })()}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 text-sm font-medium"
      >
        + Add another room usage
      </button>
    </div>
  )
}

// ============================================================================
// CREATE INVOICE MODAL
// ============================================================================

function CreateInvoiceModal({
  rooms,
  onClose,
  onCreated,
}: {
  rooms: Room[]
  onClose: () => void
  onCreated: () => void
}) {
  const [guest, setGuest] = useState<GuestOption | null>(null)
  const [lineItems, setLineItems] = useState<LineItemInput[]>([
    { roomId: rooms[0]?.id || '', startTime: '09:00', endTime: '10:00', date: '' },
  ])
  const [notes, setNotes] = useState('')
  const [sending, setSending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const calculateTotals = () => {
    let subtotal = 0
    for (const item of lineItems) {
      if (!item.roomId || !item.date || !item.startTime || !item.endTime) continue
      const room = rooms.find((r) => r.id === item.roomId)
      const rate = room?.hourlyRate || 0
      const start = new Date(`${item.date}T${item.startTime}`)
      const end = new Date(`${item.date}T${item.endTime}`)
      const hours = (end.getTime() - start.getTime()) / 3600000
      if (hours > 0) subtotal += Math.round(hours * rate * 100) / 100
    }
    const discount = guest?.discountPercent ? guest.discountPercent : 0
    const total = discount > 0 ? Math.round(subtotal * (1 - discount / 100) * 100) / 100 : subtotal
    return { subtotal, discount, total }
  }

  const { subtotal, discount, total } = calculateTotals()

  const isValid =
    guest &&
    lineItems.length > 0 &&
    lineItems.every((li) => li.roomId && li.date && li.startTime && li.endTime) &&
    total > 0

  const buildPayload = () => ({
    userId: guest!.id,
    notes: notes || undefined,
    lineItems: lineItems.map((li) => ({
      roomId: li.roomId,
      startTime: new Date(`${li.date}T${li.startTime}`).toISOString(),
      endTime: new Date(`${li.date}T${li.endTime}`).toISOString(),
    })),
  })

  const handleSaveDraft = async () => {
    if (!isValid) return
    setSaving(true)
    setError('')
    try {
      await api.invoices.create(buildPayload())
      onCreated()
    } catch (err: any) {
      setError(err.message || 'Failed to create invoice')
    } finally {
      setSaving(false)
    }
  }

  const handleSendNow = async () => {
    if (!isValid) return
    setSending(true)
    setError('')
    try {
      const { invoice } = await api.invoices.create(buildPayload())
      await api.invoices.send(invoice.id)
      onCreated()
    } catch (err: any) {
      setError(err.message || 'Failed to send invoice')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Invoice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Select Guest */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who is this invoice for?
            </label>
            <GuestPicker value={guest} onChange={setGuest} />
          </div>

          {/* Step 2: Add line items */}
          {guest && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room usage to invoice
              </label>
              <LineItemEditor items={lineItems} onChange={setLineItems} rooms={rooms} />
            </div>
          )}

          {/* Step 3: Review */}
          {guest && subtotal > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">£{subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount ({discount}%)</span>
                  <span className="text-green-600">
                    -£{(subtotal - total).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold border-t border-gray-200 pt-2">
                <span>Total</span>
                <span>£{total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500">
                Due in 14 days. Payment via Stripe.
              </p>
            </div>
          )}

          {/* Notes */}
          {guest && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (shown on invoice)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes for the guest..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={!isValid || saving || sending}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={handleSendNow}
            disabled={!isValid || saving || sending}
            className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// INVOICE DETAIL MODAL
// ============================================================================

function InvoiceDetailModal({
  invoiceId,
  onClose,
  onAction,
}: {
  invoiceId: string
  onClose: () => void
  onAction: () => void
}) {
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')
  const [confirmAction, setConfirmAction] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    api.invoices
      .get(invoiceId)
      .then((data) => setInvoice(data.invoice))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [invoiceId])

  const handleAction = async (action: string) => {
    setActionLoading(action)
    try {
      switch (action) {
        case 'send':
          await api.invoices.send(invoiceId)
          break
        case 'resend':
          await api.invoices.resend(invoiceId)
          break
        case 'cancel':
          await api.invoices.cancel(invoiceId)
          break
        case 'refund':
          await api.invoices.refund(invoiceId)
          break
        case 'delete':
          await api.invoices.delete(invoiceId)
          break
      }
      onAction()
      if (action === 'delete') {
        onClose()
      } else {
        // Refresh detail
        const data = await api.invoices.get(invoiceId)
        setInvoice(data.invoice)
      }
    } catch {
      // Error handled by API client
    } finally {
      setActionLoading('')
      setConfirmAction(null)
    }
  }

  const status = invoice?.status || 'draft'
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft!
  const StatusIcon = config!.icon

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {invoice?.invoiceRef || 'Invoice'}
            </h2>
            {invoice && (
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config!.bgColor} ${config!.color}`}>
                  <StatusIcon className="w-3 h-3" />
                  {config!.label}
                </span>
                <span className="text-sm text-gray-500">
                  for {invoice.guestName}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : invoice ? (
          <>
            <div className="p-6 space-y-6">
              {/* Invoice info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Guest</span>
                  <div className="font-medium">{invoice.guestName}</div>
                  <div className="text-gray-500">{invoice.guestEmail}</div>
                </div>
                <div>
                  <span className="text-gray-500">Created by</span>
                  <div className="font-medium">{invoice.createdByName}</div>
                </div>
                <div>
                  <span className="text-gray-500">Issued</span>
                  <div className="font-medium">
                    {invoice.sentAt
                      ? new Date(invoice.sentAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : 'Not yet sent'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Due date</span>
                  <div className="font-medium">
                    {new Date(invoice.dueDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                </div>
                {invoice.paidAt && (
                  <div>
                    <span className="text-gray-500">Paid</span>
                    <div className="font-medium text-green-600">
                      {new Date(invoice.paidAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Line items table */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Room Usage</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">
                          Room
                        </th>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">
                          Date / Time
                        </th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600">
                          Hours
                        </th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600">
                          Rate
                        </th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {invoice.lineItems.map((li) => (
                        <tr key={li.id}>
                          <td className="px-3 py-2 font-medium">{li.roomName}</td>
                          <td className="px-3 py-2 text-gray-600">
                            {new Date(li.startTime).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                            })}
                            {', '}
                            {new Date(li.startTime).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            -
                            {new Date(li.endTime).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-3 py-2 text-right">{li.hours}</td>
                          <td className="px-3 py-2 text-right">
                            £{parseFloat(li.hourlyRate).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            £{parseFloat(li.lineTotal).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>£{parseFloat(invoice.subtotalAmount).toFixed(2)}</span>
                </div>
                {invoice.discountPercent &&
                  parseFloat(invoice.discountPercent) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Discount ({invoice.discountPercent}%)
                      </span>
                      <span className="text-green-600">
                        -£
                        {(
                          parseFloat(invoice.subtotalAmount) -
                          parseFloat(invoice.totalAmount)
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                <div className="flex justify-between font-semibold text-base border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>£{parseFloat(invoice.totalAmount).toFixed(2)}</span>
                </div>
              </div>

              {/* Payment breakdown */}
              {invoice.paymentBreakdown && (
                <div className="bg-green-50 rounded-lg p-4 text-sm space-y-1">
                  <h4 className="font-medium text-green-800 mb-2">
                    Payment Breakdown
                  </h4>
                  <div className="flex justify-between">
                    <span className="text-green-700">Total charged</span>
                    <span>£{invoice.paymentBreakdown.total.toFixed(2)}</span>
                  </div>
                  {invoice.paymentBreakdown.stripeFee !== null && (
                    <div className="flex justify-between">
                      <span className="text-green-700">Stripe fee</span>
                      <span>
                        -£{invoice.paymentBreakdown.stripeFee.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {invoice.paymentBreakdown.roomsiFee !== null && (
                    <div className="flex justify-between">
                      <span className="text-green-700">
                        Roomsi fee ({invoice.paymentBreakdown.roomsiFeePercent}%)
                      </span>
                      <span>
                        -£{invoice.paymentBreakdown.roomsiFee.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {invoice.paymentBreakdown.orgReceives !== null && (
                    <div className="flex justify-between font-medium border-t border-green-200 pt-1">
                      <span className="text-green-800">You receive</span>
                      <span>
                        £{invoice.paymentBreakdown.orgReceives.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {invoice.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    {invoice.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200 bg-gray-50">
              {confirmAction ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">
                    Are you sure you want to {confirmAction} this invoice?
                  </span>
                  <button
                    onClick={() => handleAction(confirmAction)}
                    disabled={!!actionLoading}
                    className="px-3 py-1.5 text-white bg-red-600 rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => setConfirmAction(null)}
                    className="px-3 py-1.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    No
                  </button>
                </div>
              ) : (
                <>
                  {status === 'draft' && (
                    <>
                      <button
                        onClick={() => setConfirmAction('delete')}
                        className="px-3 py-1.5 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                      <button
                        onClick={() => handleAction('send')}
                        disabled={!!actionLoading}
                        className="px-4 py-1.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      >
                        <Send className="w-3.5 h-3.5" />{' '}
                        {actionLoading === 'send' ? 'Sending...' : 'Send Invoice'}
                      </button>
                    </>
                  )}
                  {(status === 'sent' || status === 'overdue') && (
                    <>
                      <button
                        onClick={() => setConfirmAction('cancel')}
                        className="px-3 py-1.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-1"
                      >
                        <Ban className="w-3.5 h-3.5" /> Cancel
                      </button>
                      <button
                        onClick={() => handleAction('resend')}
                        disabled={!!actionLoading}
                        className="px-4 py-1.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />{' '}
                        {actionLoading === 'resend' ? 'Sending...' : 'Resend'}
                      </button>
                    </>
                  )}
                  {status === 'paid' && (
                    <button
                      onClick={() => setConfirmAction('refund')}
                      className="px-4 py-1.5 text-white bg-teal-600 rounded-lg hover:bg-teal-700 text-sm font-medium flex items-center gap-1"
                    >
                      <CreditCard className="w-3.5 h-3.5" /> Refund
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-12 text-center text-gray-500">Invoice not found</div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function InvoicesPage() {
  const navigate = useNavigate()
  const {
    currentOrg,
    memberships,
    isAuthenticated,
    isLoading: authLoading,
    checkAuth,
  } = useAuthStore()

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [rooms, setRooms] = useState<Room[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null)
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login', search: { returnTo: undefined } })
    }
  }, [authLoading, isAuthenticated])

  const membership = useMemo(
    () => memberships.find((m) => m.orgId === currentOrg?.orgId),
    [memberships, currentOrg]
  )

  const isAdmin = membership?.role === 'admin'
  const isApprover = membership?.role === 'approver'

  // Check Stripe status
  useEffect(() => {
    if (!currentOrg) return
    api.stripe
      .getConnectStatus()
      .then((data) => setStripeConnected(data.connected && !!data.chargesEnabled))
      .catch(() => setStripeConnected(false))
  }, [currentOrg])

  // Load data
  const loadData = () => {
    if (!currentOrg || !stripeConnected) return
    setIsLoading(true)
    Promise.all([
      api.invoices.list(filter !== 'all' ? { status: filter } : undefined),
      api.rooms.list(),
    ])
      .then(([invData, roomData]) => {
        setInvoices(invData.invoices)
        setRooms(roomData.rooms)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    if (stripeConnected) loadData()
  }, [currentOrg, filter, stripeConnected])

  // Filter counts
  const counts = useMemo(() => {
    const c = { all: 0, draft: 0, sent: 0, paid: 0, overdue: 0, cancelled: 0, refunded: 0 }
    for (const inv of invoices) {
      c.all++
      if (inv.status in c) c[inv.status as keyof typeof c]++
    }
    return c
  }, [invoices])

  // Search
  const filteredInvoices = useMemo(() => {
    if (!search) return invoices
    const q = search.toLowerCase()
    return invoices.filter(
      (inv) =>
        inv.invoiceRef.toLowerCase().includes(q) ||
        inv.guestName.toLowerCase().includes(q) ||
        inv.guestEmail.toLowerCase().includes(q)
    )
  }, [invoices, search])

  const filterOptions = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'draft', label: 'Draft', count: counts.draft, countColor: 'gray' as const },
    { value: 'sent', label: 'Sent', count: counts.sent, countColor: 'blue' as const },
    { value: 'paid', label: 'Paid', count: counts.paid, countColor: 'primary' as const },
    { value: 'overdue', label: 'Overdue', count: counts.overdue, countColor: 'red' as const },
    { value: 'cancelled', label: 'Cancelled', count: counts.cancelled },
  ]

  if (!isAdmin && !isApprover) {
    return (
      <DashboardLayout title="Invoices">
        <div className="text-center py-12 text-gray-500">
          You do not have permission to view invoices.
        </div>
      </DashboardLayout>
    )
  }

  if (stripeConnected === false) {
    return (
      <DashboardLayout title="Invoices">
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Stripe not connected
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Invoicing requires a connected Stripe account to accept payments.
            Connect Stripe in your organisation settings to get started.
          </p>
          <button
            onClick={() => navigate({ to: '/dashboard/settings', search: { stripe: 'connect' } })}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            Go to Settings
          </button>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Invoices"
      subtitle="Create and manage invoices for room usage"
      actions={
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <FilterTabs options={filterOptions} value={filter} onChange={setFilter} />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by invoice number, guest name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        </div>

        {/* Invoice list */}
        {isLoading || stripeConnected === null ? (
          <div className="text-center py-12 text-gray-500">Loading invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {filter === 'all'
                ? 'No invoices yet. Create your first invoice to bill a guest for room usage.'
                : `No ${filter} invoices.`}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">
                    Invoice
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">
                    Guest
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">
                    Amount
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">
                    Due Date
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">
                    Items
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.map((inv) => {
                  const cfg = STATUS_CONFIG[inv.status] ?? STATUS_CONFIG.draft!
                  const Icon = cfg!.icon
                  return (
                    <tr
                      key={inv.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedInvoiceId(inv.id)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {inv.invoiceRef}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {inv.guestName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {inv.guestEmail}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg!.bgColor} ${cfg!.color}`}
                        >
                          <Icon className="w-3 h-3" />
                          {cfg!.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        £{parseFloat(inv.totalAmount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(inv.dueDate).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {inv.lineItemCount}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Eye className="w-4 h-4 text-gray-400" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateInvoiceModal
          rooms={rooms}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            loadData()
          }}
        />
      )}

      {selectedInvoiceId && (
        <InvoiceDetailModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onAction={() => loadData()}
        />
      )}
    </DashboardLayout>
  )
}
